/**
 * Device-owned order (ticket) aggregate. Business rules deliberately mirror
 * the server's `createTicket`/`addLine`/`voidTicket`/`settleWithin`
 * (apps/api/src/modules/pos/service.ts) so Phase 3 can swap the POS onto this
 * repository without changing behaviour:
 *
 *  - DINE_IN requires an active table with no other OPEN ticket; ROOM_SERVICE
 *    requires an IN_HOUSE stay.
 *  - addLine snapshots name/price/GST/HSN/station from the menu item at add
 *    time; merges into an existing QUEUED line of the same item + note;
 *    negative quantities decrement; a line at zero is removed; SERVED lines
 *    cannot be removed.
 *  - settle computes GST with the shared engine (domain/totals.ts), allocates
 *    the device INV number, writes invoice + lines, closes the order and
 *    freezes the sync payload - ALL IN ONE ATOMIC BATCH.
 *
 * Unlike the server, every order also gets a monotonic event trail
 * (order_events) - the change-signal the server model lacks (Phase 1).
 */
import { applyOffers, billDiscountOf, itemDiscountPaise, mulQty, SAC, totalise, toRupees } from '@kortex/shared';
import { LocalDataError } from '../types.js';
import { newId } from '../domain/ids.js';
import { nowIso } from '../domain/time.js';
import { computeCharges, computeSettle, qtyFromMilli, rateFromBp } from '../domain/totals.js';
import { linesForOffers, tillContext } from '../domain/till.js';
const MAX_LINE_QTY_MILLI = 100_000; // ±100 units, matching addTicketLineSchema
export class OrdersRepository {
    driver;
    context;
    sequences;
    audit;
    offerRepo;
    constructor(driver, context, sequences, audit, offerRepo) {
        this.driver = driver;
        this.context = context;
        this.sequences = sequences;
        this.audit = audit;
        this.offerRepo = offerRepo;
    }
    // ------------------------------------------------------------------
    // Reads
    // ------------------------------------------------------------------
    async getOrder(id) {
        const rows = await this.driver.query({ sql: 'SELECT * FROM orders WHERE id = ?', params: [id] });
        const order = rows[0];
        if (!order)
            return null;
        const lines = await this.driver.query({
            sql: 'SELECT * FROM order_lines WHERE order_id = ? ORDER BY fired_at, rowid',
            params: [id],
        });
        return { ...mapOrder(order), lines: lines.map(mapLine) };
    }
    async listOrders(status = 'OPEN', limit = 100) {
        const rows = status === 'ALL'
            ? await this.driver.query({
                sql: 'SELECT * FROM orders ORDER BY opened_at DESC LIMIT ?',
                params: [limit],
            })
            : await this.driver.query({
                sql: 'SELECT * FROM orders WHERE status = ? ORDER BY opened_at DESC LIMIT ?',
                params: [status, limit],
            });
        return rows.map(mapOrder);
    }
    async listEvents(orderId) {
        const rows = await this.driver.query({
            sql: 'SELECT seq, kind, at FROM order_events WHERE order_id = ? ORDER BY seq',
            params: [orderId],
        });
        return rows.map((r) => ({ seq: Number(r.seq), kind: String(r.kind), at: String(r.at) }));
    }
    // ------------------------------------------------------------------
    // Writes
    // ------------------------------------------------------------------
    async createOrder(input) {
        const covers = input.covers ?? 1;
        if (covers < 1 || covers > 50) {
            throw new LocalDataError('INVALID_COVERS', 'Covers must be between 1 and 50');
        }
        if (input.type === 'DINE_IN') {
            if (!input.tableId)
                throw new LocalDataError('TABLE_REQUIRED', 'Dine-in needs a table');
            const table = await this.driver.query({
                sql: 'SELECT code, is_active FROM restaurant_tables WHERE id = ?',
                params: [input.tableId],
            });
            if (!table[0] || Number(table[0].is_active) !== 1) {
                throw new LocalDataError('TABLE_UNKNOWN', 'That table does not exist or is inactive');
            }
            const open = await this.driver.query({
                sql: `SELECT id FROM orders WHERE table_id = ? AND status = 'OPEN' LIMIT 1`,
                params: [input.tableId],
            });
            if (open.length > 0) {
                throw new LocalDataError('TABLE_OCCUPIED', `Table ${String(table[0].code)} already has an open ticket`);
            }
        }
        if (input.type === 'ROOM_SERVICE') {
            if (!input.stayId)
                throw new LocalDataError('STAY_REQUIRED', 'Room service needs a stay');
            const stay = await this.driver.query({
                sql: `SELECT status FROM stay_snapshots WHERE id = ?`,
                params: [input.stayId],
            });
            if (!stay[0] || String(stay[0].status) !== 'IN_HOUSE') {
                throw new LocalDataError('STAY_NOT_IN_HOUSE', 'That stay is not in-house');
            }
        }
        const timezone = await this.propertyTimezone();
        const plan = this.sequences.plan('TKT', timezone);
        const id = newId();
        const now = nowIso();
        await this.driver.batch([
            ...plan.reserve,
            {
                sql: `INSERT INTO orders
                (id, property_id, number, type, status, table_id, stay_id, covers, note,
                 opened_by_user_id, opened_at, updated_at)
              VALUES (?, ?, ${plan.numberExpr}, ?, 'OPEN', ?, ?, ?, ?, ?, ?, ?)`,
                params: [
                    id,
                    this.context.propertyId,
                    ...plan.numberParams,
                    input.type,
                    input.tableId ?? null,
                    input.stayId ?? null,
                    covers,
                    input.note ?? null,
                    input.userId,
                    now,
                    now,
                ],
            },
            this.eventStatement(id, 'OPENED', { type: input.type }, input.userId, now),
        ]);
        const created = await this.getOrder(id);
        if (!created)
            throw new LocalDataError('ORDER_WRITE_LOST', 'Order not found after insert');
        return created;
    }
    /** Add (or merge/decrement) a line. Positive quantity adds, negative removes. */
    async addLine(orderId, input) {
        if (!Number.isInteger(input.quantityMilli) ||
            input.quantityMilli === 0 ||
            Math.abs(input.quantityMilli) > MAX_LINE_QTY_MILLI) {
            throw new LocalDataError('INVALID_QUANTITY', 'Quantity must be non-zero and within ±100');
        }
        const order = await this.requireOpenOrder(orderId);
        const item = await this.driver.query({
            sql: 'SELECT * FROM menu_items WHERE id = ?',
            params: [input.menuItemId],
        });
        const menuItem = item[0];
        if (!menuItem || Number(menuItem.is_active) !== 1) {
            throw new LocalDataError('ITEM_UNAVAILABLE', 'That item is not on the menu right now');
        }
        const note = (input.note ?? '').trim() || null;
        const existing = order.lines.find((l) => l.menuItemId === input.menuItemId && (l.note ?? null) === note && l.kdsState === 'QUEUED');
        const now = nowIso();
        const statements = [];
        if (existing) {
            const next = existing.quantityMilli + input.quantityMilli;
            if (next <= 0) {
                statements.push({ sql: 'DELETE FROM order_lines WHERE id = ?', params: [existing.id] });
                statements.push(this.eventStatement(orderId, 'LINE_REMOVED', { lineId: existing.id, name: existing.name }, input.userId, now));
            }
            else {
                statements.push({
                    sql: 'UPDATE order_lines SET quantity_milli = ?, updated_at = ? WHERE id = ?',
                    params: [next, now, existing.id],
                });
                statements.push(this.eventStatement(orderId, 'LINE_ADDED', { lineId: existing.id, name: existing.name, quantityMilli: input.quantityMilli }, input.userId, now));
            }
        }
        else {
            if (input.quantityMilli < 0) {
                throw new LocalDataError('LINE_NOT_FOUND', 'Nothing to decrement for that item');
            }
            const lineId = newId();
            statements.push({
                sql: `INSERT INTO order_lines
                (id, order_id, menu_item_id, name, station, quantity_milli, unit_price_paise,
                 discount_pct_bp, gst_rate_bp, hsn, note, fired_at, updated_at)
              VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
                params: [
                    lineId,
                    orderId,
                    String(menuItem.id),
                    String(menuItem.name),
                    String(menuItem.station),
                    input.quantityMilli,
                    Number(menuItem.price_paise),
                    Number(menuItem.discount_pct_bp),
                    Number(menuItem.gst_rate_bp),
                    String(menuItem.hsn),
                    note,
                    now,
                    now,
                ],
            });
            statements.push(this.eventStatement(orderId, 'LINE_ADDED', { lineId, name: String(menuItem.name), quantityMilli: input.quantityMilli }, input.userId, now));
        }
        statements.push({
            sql: `UPDATE orders SET updated_at = ? WHERE id = ? AND status = 'OPEN'`,
            params: [now, orderId],
        });
        await this.driver.batch(statements);
        const updated = await this.getOrder(orderId);
        if (!updated)
            throw new LocalDataError('ORDER_WRITE_LOST', 'Order vanished during line update');
        return updated;
    }
    async removeLine(orderId, lineId, userId) {
        const order = await this.requireOpenOrder(orderId);
        const line = order.lines.find((l) => l.id === lineId);
        if (!line)
            throw new LocalDataError('LINE_NOT_FOUND', 'That line is not on the ticket');
        if (line.kdsState === 'SERVED') {
            throw new LocalDataError('LINE_SERVED', 'A served item cannot be removed - void the ticket instead');
        }
        const now = nowIso();
        await this.driver.batch([
            { sql: 'DELETE FROM order_lines WHERE id = ?', params: [lineId] },
            this.eventStatement(orderId, 'LINE_REMOVED', { lineId, name: line.name }, userId, now),
            { sql: `UPDATE orders SET updated_at = ? WHERE id = ?`, params: [now, orderId] },
        ]);
        const updated = await this.getOrder(orderId);
        if (!updated)
            throw new LocalDataError('ORDER_WRITE_LOST', 'Order vanished during line removal');
        return updated;
    }
    async voidOrder(orderId, reason, userId) {
        if (!reason.trim())
            throw new LocalDataError('REASON_REQUIRED', 'A void needs a reason');
        await this.requireOpenOrder(orderId);
        const now = nowIso();
        const results = await this.driver.batch([
            {
                sql: `UPDATE orders SET status = 'VOID', void_reason = ?, closed_at = ?, updated_at = ?
              WHERE id = ? AND status = 'OPEN'`,
                params: [reason.trim(), now, now, orderId],
            },
            this.eventStatement(orderId, 'VOIDED', { reason: reason.trim() }, userId, now),
        ]);
        if ((results[0]?.changes ?? 0) !== 1) {
            throw new LocalDataError('ALREADY_SETTLED', 'This ticket has already been settled');
        }
        await this.audit.record({
            userId,
            action: 'pos.ticket_voided',
            entity: 'Order',
            entityId: orderId,
            summary: `Ticket voided: ${reason.trim()}`,
        });
    }
    /**
     * Settle the order: shared-engine GST, device INV number, invoice + lines,
     * order close, BILLED event and the frozen sync operation - one atomic
     * batch. This is the transaction the whole local layer exists to make
     * durable (failure scenarios 3/4).
     */
    async settle(orderId, input, syncQueue) {
        const order = await this.requireOpenOrder(orderId);
        if (order.lines.length === 0) {
            throw new LocalDataError('EMPTY_ORDER', 'Add at least one item before billing');
        }
        if (input.mode === 'ROOM' && !order.stayId) {
            throw new LocalDataError('NO_STAY', 'Room posting needs a ticket attached to a stay');
        }
        const property = await this.propertyProfile();
        const guestStateCode = input.guestGstin ? input.guestGstin.slice(0, 2) : input.guestStateCode;
        const interState = Boolean(guestStateCode && guestStateCode !== property.stateCode);
        const timezone = property.timezone;
        // Offers first, then the cashier's discount on what is left - mirrors the
        // server's settleWithin exactly (apps/api/src/modules/pos/service.ts).
        const till = await tillContext(this.driver, this.offerRepo);
        const offerLines = await linesForOffers(this.driver, order.lines);
        const offersOutcome = applyOffers({ lines: offerLines, offers: till.offers, at: new Date(), timeZone: timezone });
        const lineInputs = order.lines.map((l) => ({
            unitPricePaise: l.unitPricePaise,
            quantityMilli: l.quantityMilli,
            discountPctBp: l.discountPctBp,
            gstRateBp: l.gstRateBp,
        }));
        // A percentage is worked out here rather than at the counter, so it is
        // taken against the ticket as it stands at the moment of billing - same
        // duplicated gross/itemDiscount computation the server accepts too
        // (settleWithin computes it inline, then taxedLines recomputes it).
        const gross = order.lines.reduce((sum, l) => sum + mulQty(l.unitPricePaise, qtyFromMilli(l.quantityMilli)), 0);
        const itemDiscountTotal = order.lines.reduce((sum, l) => sum + itemDiscountPaise(l.unitPricePaise, qtyFromMilli(l.quantityMilli), rateFromBp(l.discountPctBp)), 0);
        const discountable = Math.max(0, gross - itemDiscountTotal - offersOutcome.total);
        const billDiscount = input.discountPct !== undefined
            ? billDiscountOf(discountable, input.discountPct)
            : (input.billDiscountPaise ?? 0);
        const { taxed } = computeSettle(lineInputs, billDiscount, interState, offersOutcome.perLine);
        // Packing, delivery or service, added after every discount and taxed with
        // the food - a discount is on the meal, not on the box it leaves in.
        const chargeLines = computeCharges(taxed, { orderType: order.type, settings: till.charges, interState });
        const totals = totalise([...taxed, ...chargeLines.map((c) => c.taxed)]);
        // Allocated up front (its own atomic reserve-and-read, sequences.ts) so the
        // resulting human-readable number can be frozen into the sync payload below
        // - a Phase 4 requirement the Phase 2/3 plan()+numberExpr-in-batch style
        // could not satisfy, because that form only knows the number inside SQL,
        // never back in JS where the payload is built. Same "a lost race burns a
        // number" acceptance already documented for this series (sequences.ts).
        const invoiceNumber = await this.sequences.next('INV', timezone);
        const invoiceId = newId();
        const now = nowIso();
        const status = input.mode === 'ROOM' ? 'POSTED_TO_ROOM' : 'PAID';
        const orderStatus = input.mode === 'ROOM' ? 'POSTED_TO_ROOM' : 'BILLED';
        // Freeze the sync payload now - it commits atomically with the rows.
        const payload = {
            order: {
                id: order.id,
                number: order.number,
                type: order.type,
                status: orderStatus,
                tableId: order.tableId,
                stayId: order.stayId,
                covers: order.covers,
                note: order.note,
                openedByUserId: order.openedByUserId,
                openedAt: order.openedAt,
                closedAt: now,
                lines: order.lines.map((l, i) => ({
                    id: l.id,
                    menuItemId: l.menuItemId,
                    name: l.name,
                    station: l.station,
                    quantityMilli: l.quantityMilli,
                    unitPricePaise: l.unitPricePaise,
                    discountPctBp: l.discountPctBp,
                    gstRateBp: l.gstRateBp,
                    hsn: l.hsn,
                    note: l.note,
                    kdsState: l.kdsState,
                    firedAt: l.firedAt,
                    discountPaise: taxed[i]?.discount ?? 0,
                    offerDiscountPaise: offersOutcome.perLine[i] ?? 0,
                })),
            },
            invoice: {
                id: invoiceId,
                number: invoiceNumber,
                kind: 'FNB',
                status,
                guestName: input.guestName ?? 'Walk-in',
                guestGstin: input.guestGstin ?? null,
                placeOfSupply: guestStateCode ?? property.stateCode,
                isInterState: interState,
                subtotalPaise: totals.subtotal,
                discountPaise: totals.discount,
                cgstPaise: totals.cgst,
                sgstPaise: totals.sgst,
                igstPaise: totals.igst,
                roundOffPaise: totals.roundOff,
                totalPaise: totals.total,
                mode: input.mode,
                issuedByUserId: input.userId,
                issuedAt: now,
                discountReason: input.discountReason ?? '',
            },
            // Kept on the invoice as they stood on the day - an offer can be edited
            // or withdrawn, and a reprint months later still has to say what the
            // guest was actually given (mirrors the server's Invoice.offers).
            offers: offersOutcome.applied.map((a) => ({
                offerId: a.offerId,
                name: a.name,
                type: a.type,
                discountPaise: a.discount,
                times: a.times,
            })),
            charges: chargeLines.map(({ charge, taxed: t }) => ({
                key: charge.key,
                label: charge.label,
                amountPaise: charge.amount,
                gstRateBp: Math.round(t.gstRate * 100),
            })),
        };
        const prepared = await syncQueue.prepare('SALE_AGGREGATE', order.id, payload);
        const statements = [
            {
                sql: `INSERT INTO invoices
                (id, property_id, number, kind, status, order_id, guest_name, guest_gstin,
                 place_of_supply, is_inter_state, subtotal_paise, discount_paise, cgst_paise,
                 sgst_paise, igst_paise, round_off_paise, total_paise, mode,
                 issued_by_user_id, issued_at, sync_state, offers_json)
              VALUES (?, ?, ?, 'FNB', ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'QUEUED', ?)`,
                params: [
                    invoiceId,
                    this.context.propertyId,
                    invoiceNumber,
                    status,
                    order.id,
                    input.guestName ?? 'Walk-in',
                    input.guestGstin ?? null,
                    guestStateCode ?? property.stateCode,
                    interState ? 1 : 0,
                    totals.subtotal,
                    totals.discount,
                    totals.cgst,
                    totals.sgst,
                    totals.igst,
                    totals.roundOff,
                    totals.total,
                    input.mode,
                    input.userId,
                    now,
                    offersOutcome.applied.length > 0
                        ? JSON.stringify(offersOutcome.applied.map((a) => ({ name: a.name, times: a.times, discount: toRupees(a.discount) })))
                        : null,
                ],
            },
            ...order.lines.map((l, i) => ({
                sql: `INSERT INTO invoice_lines
                (id, invoice_id, description, hsn, quantity_milli, unit_price_paise, gst_rate_bp,
                 discount_paise, taxable_paise, cgst_paise, sgst_paise, igst_paise, total_paise)
              VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
                params: [
                    newId(),
                    invoiceId,
                    l.name,
                    l.hsn,
                    l.quantityMilli,
                    l.unitPricePaise,
                    l.gstRateBp,
                    taxed[i]?.discount ?? 0,
                    taxed[i]?.taxable ?? 0,
                    taxed[i]?.cgst ?? 0,
                    taxed[i]?.sgst ?? 0,
                    taxed[i]?.igst ?? 0,
                    taxed[i]?.total ?? 0,
                ],
            })),
            // Charges are billed lines like any other, so they show on the ticket,
            // the invoice and the printed receipt - same as serialiseOrder's own
            // settle path server-side.
            ...chargeLines.map(({ charge, taxed: t }) => ({
                sql: `INSERT INTO invoice_lines
                (id, invoice_id, description, hsn, quantity_milli, unit_price_paise, gst_rate_bp,
                 discount_paise, taxable_paise, cgst_paise, sgst_paise, igst_paise, total_paise)
              VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
                params: [
                    newId(),
                    invoiceId,
                    charge.label,
                    SAC.RESTAURANT,
                    1000,
                    charge.amount,
                    Math.round(t.gstRate * 100),
                    0,
                    t.taxable,
                    t.cgst,
                    t.sgst,
                    t.igst,
                    t.total,
                ],
            })),
            {
                sql: `UPDATE orders SET status = ?, closed_at = ?, updated_at = ?, sync_state = 'QUEUED'
              WHERE id = ? AND status = 'OPEN'`,
                params: [orderStatus, now, now, order.id],
            },
            this.eventStatement(order.id, 'BILLED', { invoiceId, mode: input.mode, totalPaise: totals.total }, input.userId, now),
            prepared.statement,
        ];
        const results = await this.driver.batch(statements);
        const orderUpdate = results[1 + order.lines.length + chargeLines.length];
        if ((orderUpdate?.changes ?? 0) !== 1) {
            // The guarded UPDATE saw a non-OPEN order: a concurrent settle won the
            // race after our pre-read. The batch committed rows for a stale view -
            // roll them back compensatingly and report. Single-writer devices never
            // hit this path; it exists as defence in depth.
            await this.driver.batch([
                { sql: 'DELETE FROM invoice_lines WHERE invoice_id = ?', params: [invoiceId] },
                { sql: 'DELETE FROM invoices WHERE id = ?', params: [invoiceId] },
                { sql: 'DELETE FROM sync_queue WHERE op_id = ?', params: [prepared.opId] },
                { sql: `DELETE FROM order_events WHERE order_id = ? AND kind = 'BILLED' AND at = ?`, params: [order.id, now] },
            ]);
            throw new LocalDataError('ALREADY_SETTLED', 'This ticket has already been settled');
        }
        await this.audit.record({
            userId: input.userId,
            action: 'pos.ticket_billed',
            entity: 'Invoice',
            entityId: invoiceId,
            summary: `Ticket ${order.number} billed as ${invoiceNumber} (${input.mode})`,
            metadataJson: JSON.stringify({ orderNumber: order.number, totalPaise: totals.total }),
        });
        if (billDiscount > 0) {
            await this.audit.record({
                userId: input.userId,
                action: 'pos.discount_applied',
                entity: 'Invoice',
                entityId: invoiceId,
                summary: `Discount of ${billDiscount} paise: ${input.discountReason ?? ''}`,
            });
        }
        return { invoiceId, invoiceNumber, totalPaise: totals.total, opId: prepared.opId };
    }
    // ------------------------------------------------------------------
    async requireOpenOrder(orderId) {
        const order = await this.getOrder(orderId);
        if (!order)
            throw new LocalDataError('ORDER_NOT_FOUND', 'That ticket does not exist');
        if (order.status !== 'OPEN') {
            throw new LocalDataError('ALREADY_SETTLED', 'This ticket has already been settled');
        }
        return order;
    }
    eventStatement(orderId, kind, payload, userId, at) {
        return {
            sql: `INSERT INTO order_events (id, order_id, seq, kind, payload_json, by_user_id, at)
            VALUES (?, ?, (SELECT COALESCE(MAX(seq), 0) + 1 FROM order_events WHERE order_id = ?), ?, ?, ?, ?)`,
            params: [newId(), orderId, orderId, kind, JSON.stringify(payload), userId, at],
        };
    }
    async propertyTimezone() {
        const rows = await this.driver.query({ sql: 'SELECT timezone FROM property LIMIT 1' });
        return rows[0] ? String(rows[0].timezone) : 'Asia/Kolkata';
    }
    async propertyProfile() {
        const rows = await this.driver.query({ sql: 'SELECT state_code, timezone FROM property LIMIT 1' });
        const row = rows[0];
        if (!row) {
            throw new LocalDataError('PROPERTY_NOT_PROVISIONED', 'The property profile has not been provisioned on this device');
        }
        return { stateCode: String(row.state_code), timezone: String(row.timezone) };
    }
}
function mapOrder(r) {
    return {
        id: String(r.id),
        propertyId: String(r.property_id),
        number: String(r.number),
        type: String(r.type),
        status: String(r.status),
        tableId: r.table_id == null ? null : String(r.table_id),
        stayId: r.stay_id == null ? null : String(r.stay_id),
        covers: Number(r.covers),
        note: r.note == null ? null : String(r.note),
        voidReason: r.void_reason == null ? null : String(r.void_reason),
        openedByUserId: String(r.opened_by_user_id),
        openedAt: String(r.opened_at),
        closedAt: r.closed_at == null ? null : String(r.closed_at),
        updatedAt: String(r.updated_at),
        syncState: String(r.sync_state),
    };
}
function mapLine(r) {
    return {
        id: String(r.id),
        orderId: String(r.order_id),
        menuItemId: String(r.menu_item_id),
        name: String(r.name),
        station: String(r.station),
        quantityMilli: Number(r.quantity_milli),
        unitPricePaise: Number(r.unit_price_paise),
        discountPctBp: Number(r.discount_pct_bp),
        gstRateBp: Number(r.gst_rate_bp),
        hsn: String(r.hsn),
        note: r.note == null ? null : String(r.note),
        kdsState: String(r.kds_state),
        firedAt: String(r.fired_at),
        updatedAt: String(r.updated_at),
    };
}
//# sourceMappingURL=orders.js.map